// useInspection.ts
import { useEffect } from 'react';
import { inspectionDB } from '../data/database.structure';
import { ObservableItemIdentifier } from '../api-contracts/other/ObservableItemIdentifier';
import { InspectionModelHelpers } from '../helpers/inspection-model-helpers';
import useMergedState from './useMergedState';

interface UndercarriageChangeout {
  changeoutDate: Date;
  changeoutHours: number;
}

interface OperatingHoursState {
  operatingHoursError: string | undefined;
  lastInspectionDate: Date | undefined;
  nextInspectionDate: Date | undefined;
  lastOperatingHoursDataLoading: boolean;
  lastTotalHours: number | undefined;
  /** Date of the inspection that carried lastTotalHours (for the h/day plausibility rule). */
  lastTotalHoursDate: Date | undefined;
  nextTotalHours: number | undefined;
  lastLeftUndercarriageChangeout: UndercarriageChangeout | undefined;
  nextLeftUndercarriageChangeout: UndercarriageChangeout | undefined;
  lastRightUndercarriageChangeout: UndercarriageChangeout | undefined;
  nextRightUndercarriageChangeout: UndercarriageChangeout | undefined;
  lastInspectionMachineHours: number | undefined;
}

const useOperatingHours = ({
  inspectionId,
  assetId,
  inspectionDate,
}: {
  inspectionId: string;
  assetId?: number | null;
  inspectionDate?: Date | null;
}) => {
  const [state, setState] = useMergedState<OperatingHoursState>({
    operatingHoursError: undefined,
    lastInspectionDate: undefined,
    nextInspectionDate: undefined,
    lastOperatingHoursDataLoading: true,
    lastTotalHours: undefined,
    lastTotalHoursDate: undefined,
    nextTotalHours: undefined,
    lastLeftUndercarriageChangeout: undefined,
    nextLeftUndercarriageChangeout: undefined,
    lastRightUndercarriageChangeout: undefined,
    nextRightUndercarriageChangeout: undefined,
    lastInspectionMachineHours: undefined,
  });

  useEffect(() => {
    setState({
      lastOperatingHoursDataLoading: true,
      operatingHoursError: undefined,
      lastTotalHours: undefined,
      lastTotalHoursDate: undefined,
      lastLeftUndercarriageChangeout: undefined,
      lastRightUndercarriageChangeout: undefined,
    });

    async function getInspection(id: number) {
      try {
        const sortedInspections = (
          await inspectionDB.inspections.where('assetId').equals(id).toArray()
        )
          .sort(
            (a, b) => b.inspectionDate.getTime() - a.inspectionDate.getTime(),
          )
          .filter(
            // Inspections synced without category types (#10006) carry no
            // observations, so they cannot contribute operating hours.
            (i) =>
              i.assetId === id &&
              i.inspectionDate < (inspectionDate ?? Date.now()) &&
              i.externalId !== inspectionId &&
              InspectionModelHelpers.hasCategoryTypes(i),
          );

        const previousInspections = sortedInspections.filter(
          (i) =>
            i.inspectionDate < (inspectionDate ?? Date.now()) &&
            i.externalId !== inspectionId,
        );
        const nextInspections = sortedInspections.filter(
          (i) =>
            i.inspectionDate > (inspectionDate ?? Date.now()) &&
            i.externalId !== inspectionId,
        );

        if (previousInspections.length > 0) {
          const lastInspection = previousInspections[0];
          const lastInspectionMachineHoursObservation =
            lastInspection.inspectionCategoryTypes?.[0]?.observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .TotalMachineHours,
            );

          const update: Partial<OperatingHoursState> = {
            lastInspectionDate: lastInspection.inspectionDate,
          };

          if (lastInspectionMachineHoursObservation?.value) {
            update.lastInspectionMachineHours = parseInt(
              lastInspectionMachineHoursObservation.value,
            );
          }

          setState((prev) => ({ ...prev, ...update }));
        }

        if (nextInspections.length > 0) {
          setState((prev) => ({
            ...prev,
            nextInspectionDate: nextInspections[0].inspectionDate,
          }));
        }

        // Get the last inspection that had total hours
        const lastInspectionWithTotalHours = previousInspections.find((i) =>
          i.inspectionCategoryTypes[0].observations.some(
            (o) =>
              o.observableItem.identifier ===
              ObservableItemIdentifier.Identifiers.Operation.TotalMachineHours,
          ),
        );
        if (lastInspectionWithTotalHours) {
          const value =
            lastInspectionWithTotalHours.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .TotalMachineHours,
            )?.value;
          if (value) {
            setState({
              lastTotalHours: parseInt(value),
              lastTotalHoursDate: lastInspectionWithTotalHours.inspectionDate,
            });
          }
        }

        // ObservableItemIdentifier.Identifiers.Operation.UndercarriageChangeOut.Left.MachineHoursAtChange;

        const leftUndercarriageChangeout = previousInspections.find((i) =>
          i.inspectionCategoryTypes[0].observations.some(
            (o) =>
              o.observableItem.identifier ===
              ObservableItemIdentifier.Identifiers.Operation
                .UndercarriageChangeOut.Left.MachineHoursAtChange,
          ),
        );
        if (leftUndercarriageChangeout) {
          const value =
            leftUndercarriageChangeout.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .UndercarriageChangeOut.Left.MachineHoursAtChange,
            )?.value;
          const date =
            leftUndercarriageChangeout.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .UndercarriageChangeOut.Left.Date,
            )?.value;
          if (value && date) {
            const leftDate = new Date(date);
            setState({
              lastLeftUndercarriageChangeout: {
                changeoutDate: leftDate,
                changeoutHours: parseInt(value),
              },
            });
          }
        }

        const nextLeftUndercarriageChangeout = nextInspections.find((i) =>
          i.inspectionCategoryTypes[0].observations.some(
            (o) =>
              o.observableItem.identifier ===
              ObservableItemIdentifier.Identifiers.Operation
                .UndercarriageChangeOut.Left.MachineHoursAtChange,
          ),
        );
        if (nextLeftUndercarriageChangeout) {
          const value =
            nextLeftUndercarriageChangeout.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .UndercarriageChangeOut.Left.MachineHoursAtChange,
            )?.value;
          const date =
            nextLeftUndercarriageChangeout.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .UndercarriageChangeOut.Left.Date,
            )?.value;
          if (value && date) {
            setState({
              nextLeftUndercarriageChangeout: {
                changeoutDate: date ? new Date(date) : new Date(),
                changeoutHours: parseInt(value),
              },
            });
          }
        }

        // ObservableItemIdentifier.Identifiers.Operation.UndercarriageChangeOut.Right.MachineHoursAtChange;

        const rightUndercarriageChangeout = previousInspections.find((i) =>
          i.inspectionCategoryTypes[0].observations.some(
            (o) =>
              o.observableItem.identifier ===
              ObservableItemIdentifier.Identifiers.Operation
                .UndercarriageChangeOut.Right.MachineHoursAtChange,
          ),
        );
        if (rightUndercarriageChangeout) {
          const value =
            rightUndercarriageChangeout.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .UndercarriageChangeOut.Right.MachineHoursAtChange,
            )?.value;
          const date =
            rightUndercarriageChangeout.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .UndercarriageChangeOut.Right.Date,
            )?.value;
          if (value && date) {
            setState({
              lastRightUndercarriageChangeout: {
                changeoutDate: date ? new Date(date) : new Date(),
                changeoutHours: parseInt(value),
              },
            });
          }
        }

        const nextRightUndercarriageChangeout = nextInspections.find((i) =>
          i.inspectionCategoryTypes[0].observations.some(
            (o) =>
              o.observableItem.identifier ===
              ObservableItemIdentifier.Identifiers.Operation
                .UndercarriageChangeOut.Right.MachineHoursAtChange,
          ),
        );
        if (nextRightUndercarriageChangeout) {
          const value =
            nextRightUndercarriageChangeout.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .UndercarriageChangeOut.Right.MachineHoursAtChange,
            )?.value;
          const date =
            nextRightUndercarriageChangeout.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .UndercarriageChangeOut.Right.Date,
            )?.value;
          if (value && date) {
            setState({
              nextRightUndercarriageChangeout: {
                changeoutDate: date ? new Date(date) : new Date(),
                changeoutHours: parseInt(value),
              },
            });
          }
        }

        // ObservableItemIdentifier.Identifiers.Operation.TotalMachineHours;

        const nextInspectionWithTotalHours = nextInspections.find((i) =>
          i.inspectionCategoryTypes[0].observations.some(
            (o) =>
              o.observableItem.identifier ===
              ObservableItemIdentifier.Identifiers.Operation.TotalMachineHours,
          ),
        );
        if (nextInspectionWithTotalHours) {
          const value =
            nextInspectionWithTotalHours.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .TotalMachineHours,
            )?.value;
          if (value) {
            setState({
              nextTotalHours: parseInt(value),
            });
          }
        }

        const previousInspectionWithTotalHours = previousInspections.find((i) =>
          i.inspectionCategoryTypes[0].observations.some(
            (o) =>
              o.observableItem.identifier ===
              ObservableItemIdentifier.Identifiers.Operation.TotalMachineHours,
          ),
        );
        if (previousInspectionWithTotalHours) {
          const value =
            previousInspectionWithTotalHours.inspectionCategoryTypes[0].observations.find(
              (o) =>
                o.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Operation
                  .TotalMachineHours,
            )?.value;
          if (value) {
            setState({
              lastTotalHours: parseInt(value),
              lastTotalHoursDate:
                previousInspectionWithTotalHours.inspectionDate,
            });
          }
        }

        // Typed undercarriage change-out read model (#10223 native adoption): the
        // server's events for this asset's sides - fleet-wide truth the local
        // inspection mining above cannot see (e.g. change-outs recorded by admins
        // or by inspections this device never held). The local mining still covers
        // unsynced captures; the merge takes whichever statement is nearer.
        const crawlerTypeIds = new Set(
          (await inspectionDB.equipmentTypes.toArray())
            .filter((type) => type.name === 'Crawler')
            .map((type) => type.id),
        );
        const sides = (
          await inspectionDB.equipments.where('assetId').equals(id).toArray()
        ).filter((equipment) => crawlerTypeIds.has(equipment.equipmentTypeId));
        const syncedSideIds = sides
          .filter((side) => side.id > 0)
          .map((side) => side.id);
        const sideEvents =
          syncedSideIds.length > 0
            ? await inspectionDB.undercarriageChangeOuts
                .where('undercarriageSideEquipmentId')
                .anyOf(syncedSideIds)
                .toArray()
            : [];

        if (sideEvents.length > 0) {
          const referenceDate = inspectionDate
            ? new Date(inspectionDate)
            : new Date();
          // Anchor at local noon so the yyyy-MM-dd wire date can't shift a day.
          const toLocalDate = (wireDate: string) =>
            new Date(`${wireDate}T12:00:00`);
          const toChangeout = (
            event: (typeof sideEvents)[number],
          ): UndercarriageChangeout => ({
            changeoutDate: toLocalDate(event.changeOutDate),
            changeoutHours: event.machineHoursAtChangeOut ?? 0,
          });
          const eventsForSide = (prefix: string) => {
            const ids = new Set(
              sides
                .filter((side) => side.name?.toLowerCase().startsWith(prefix))
                .map((side) => side.id),
            );
            return sideEvents.filter((event) =>
              ids.has(event.undercarriageSideEquipmentId),
            );
          };
          const latestBefore = (events: typeof sideEvents) =>
            events
              .map(toChangeout)
              .filter((event) => event.changeoutDate < referenceDate)
              .sort(
                (a, b) => b.changeoutDate.getTime() - a.changeoutDate.getTime(),
              )[0];
          const earliestAfter = (events: typeof sideEvents) =>
            events
              .map(toChangeout)
              .filter((event) => event.changeoutDate > referenceDate)
              .sort(
                (a, b) => a.changeoutDate.getTime() - b.changeoutDate.getTime(),
              )[0];
          const nearerLast = (
            a?: UndercarriageChangeout,
            b?: UndercarriageChangeout,
          ) => (!a ? b : !b ? a : a.changeoutDate >= b.changeoutDate ? a : b);
          const nearerNext = (
            a?: UndercarriageChangeout,
            b?: UndercarriageChangeout,
          ) => (!a ? b : !b ? a : a.changeoutDate <= b.changeoutDate ? a : b);

          const leftEvents = eventsForSide('left');
          const rightEvents = eventsForSide('right');

          setState((prev) => ({
            ...prev,
            lastLeftUndercarriageChangeout: nearerLast(
              prev.lastLeftUndercarriageChangeout,
              latestBefore(leftEvents),
            ),
            nextLeftUndercarriageChangeout: nearerNext(
              prev.nextLeftUndercarriageChangeout,
              earliestAfter(leftEvents),
            ),
            lastRightUndercarriageChangeout: nearerLast(
              prev.lastRightUndercarriageChangeout,
              latestBefore(rightEvents),
            ),
            nextRightUndercarriageChangeout: nearerNext(
              prev.nextRightUndercarriageChangeout,
              earliestAfter(rightEvents),
            ),
          }));
        }

        // const previousInspections = sortedInspections.filter((i) => i.assetId === id);
        // const previousInspection = sortedInspections.sort((a, b) => a.inspectionDate.getSeconds() - b.inspectionDate.getSeconds()).findLast(
        //   (i) => i.assetId === id && i.inspectionDate < (inspectionDate ?? Date.now()) && i.externalId !== inspectionId
        // );
        // if (previousInspection) {
        //   setInspection(previousInspection);
        // } else {
        //   setInspectionError('Inspection not found');
        // }
      } catch (error) {
        console.error('Error fetching prev inspection', error);
        setState({
          operatingHoursError: 'Error fetching prev inspection',
        });
      } finally {
        setState({
          lastOperatingHoursDataLoading: false,
        });
      }
    }

    if (assetId) {
      getInspection(assetId);
    }
    // Depend on the date VALUE, not the Date object: every inspection-cache refetch
    // (each field blur invalidates it) builds a new Date instance, and re-running this
    // effect on identity flipped lastOperatingHoursDataLoading to true - the Operation
    // screen's early return then swapped the whole tree for a skeleton, unmounting every
    // input mid-entry (focus loss, scroll-to-top flicker) and blanking lastTotalHours so
    // the machine-hours validation silently passed (#10549).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, inspectionDate?.getTime()]);

  return state;
};

export default useOperatingHours;
